import { useEffect, useMemo, useState } from "react";
import { debounce } from "lodash";

// EN-1147: the day-view calendar grid and the Quick Start panel both switch to a
// narrow-viewport layout below this width — the widths the responsive audit confirmed
// broken (320/375/414px) and unaffected above (768px+).
export const NARROW_VIEWPORT_MAX_WIDTH = 480;

// PR review: this hook mounts in several hot components (RecordingWidget, DateToolbar,
// VirtualizedResourceCalendar). A raw `resize` listener fires many events per second while
// dragging a window or during some mobile keyboard show/hide sequences — debouncing coalesces
// that into one handler invocation per pause, instead of re-invoking (and re-reading
// window.innerWidth) on every single event.
export const RESIZE_DEBOUNCE_MS = 150;

export function useIsNarrowViewport(maxWidth: number = NARROW_VIEWPORT_MAX_WIDTH): boolean {
    const [isNarrow, setIsNarrow] = useState(() => window.innerWidth <= maxWidth);

    const handleResize = useMemo(
        () => debounce(() => setIsNarrow(window.innerWidth <= maxWidth), RESIZE_DEBOUNCE_MS),
        [maxWidth],
    );

    useEffect(() => {
        // Not debounced: apply immediately on mount (or when maxWidth changes) rather than
        // waiting out a debounce delay for the initial/synced value.
        setIsNarrow(window.innerWidth <= maxWidth);
        window.addEventListener("resize", handleResize);
        return () => {
            window.removeEventListener("resize", handleResize);
            handleResize.cancel();
        };
    }, [maxWidth, handleResize]);

    return isNarrow;
}
